import { useMemo, useRef, useState } from 'react';
import s from './Map.module.css';
import { roadmap as roadmapData } from '../data/roadmap';
import type { ITask } from '../data/types';
import { Node } from './Node';
import { Connections } from './Connections';
import { PhaseGroup } from './PhaseGroup';
import { usePanZoom } from '../utils/usePanZoom';
import { Header } from './Header';
import { Filters, type TStatusFilter } from './Filters';
import { BottomSheet } from './BottomSheet';

export const Map = () => {
  const { containerRef, state, setState } = usePanZoom({
    minScale: 0.25,
    maxScale: 2.5,
    initial: { scale: 0.5, x: 200, y: 132 },
  });

  const [statusFilter, setStatusFilter] = useState<TStatusFilter>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<ITask | null>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const allTasks = useMemo(() => roadmapData.phases.flatMap((p) => p.tasks), []);

  const visibleSet = useMemo(() => {
    const q = search.trim().toLowerCase();
    return new Set(
      allTasks
        .filter((t) => {
          if (statusFilter !== 'all' && t.status !== statusFilter) return false;
          if (q) {
            const haystack =
              `${t.title} ${t.description} ${t.endpoints?.join(' ') ?? ''} ${t.pages?.join(' ') ?? ''}`.toLowerCase();
            if (!haystack.includes(q)) return false;
          }
          return true;
        })
        .map((t) => t.id),
    );
  }, [allTasks, statusFilter, search]);

  const handleZoom = (factor: number) => {
    setState((prev) => {
      const el = containerRef.current;
      if (!el) return prev;
      const rect = el.getBoundingClientRect();
      const cx = rect.width / 2;
      const cy = rect.height / 2;
      const nextScale = Math.max(0.25, Math.min(2.5, prev.scale * factor));
      const ratio = nextScale / prev.scale;
      return {
        scale: nextScale,
        x: cx - (cx - prev.x) * ratio,
        y: cy - (cy - prev.y) * ratio,
      };
    });
  };

  const handleReset = () => setState({ scale: 0.5, x: 200, y: 132 });

  return (
    <>
      <div ref={headerRef}>
        <Header
          roadmap={roadmapData}
          onZoomIn={() => handleZoom(1.2)}
          onZoomOut={() => handleZoom(0.83)}
          onReset={handleReset}
        />
      </div>

      <Filters
        status={statusFilter}
        onStatusChange={setStatusFilter}
        search={search}
        onSearchChange={setSearch}
      />

      <div ref={containerRef} className={s.canvas}>
        <div
          className={s.world}
          style={{
            transform: `translate3d(${state.x}px, ${state.y}px, 0) scale(${state.scale})`,
          }}
        >
          <div className={s.grid} aria-hidden="true" />

          {roadmapData.phases.map((phase) => {
            const visibleInPhase = phase.tasks.filter((t) => visibleSet.has(t.id));
            const isPhaseDimmed =
              visibleInPhase.length === 0 && (statusFilter !== 'all' || search.trim() !== '');
            return (
              <PhaseGroup
                key={phase.id}
                phase={phase}
                tasks={phase.tasks}
                isDimmed={isPhaseDimmed}
              />
            );
          })}

          <Connections tasks={allTasks} visibleIds={visibleSet} />

          {allTasks.map((task) => (
            <Node
              key={task.id}
              task={task}
              isDimmed={!visibleSet.has(task.id)}
              onSelect={setSelected}
            />
          ))}
        </div>
      </div>

      <BottomSheet task={selected} onClose={() => setSelected(null)} />
    </>
  );
};
